import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import CamelCase from 'camelcase';
import camelCaseRecursive from 'camelcase-keys-recursive';
import { withFormik, Form, Field, getIn } from 'formik';
import { FormikInput, ButtonTooltip, FormikRangePicker, FormikSelect, InputSelect } from '../../components';
import { documentStatus, documentTypes } from './DocumentForm/FormOptions';

import { debounceIndexEmployeesRequest, searchEmployeeRequest } from '../../requests/employees';
import { debounceIndexDocumentTemplatesRequest } from '../../requests/documentTemplates';

const ALL_SELECTED_LABEL = 'Todos';

const DocumentFilter = ({ values, errors, touched, setFieldValue }) => {
  const dispatch = useDispatch();
  const [selector, setSelector] = useState('');

  const fetchDocumentTemplates = (inputValue, callback) => {
    debounceIndexDocumentTemplatesRequest({
      dispatch,
      params: {
        active: true,
        sort_column: 'name',
        query: inputValue,
        display_length: 20
      },
      successCallback: response => {
        callback(response.data.data);
      }
    });
  };

  const searchEmployeesParams = () => {
    const request = async () =>
      searchEmployeeRequest({
        dispatch,
        params: {
          sort_column: 'name',
          paginate: false,
          advance: false
        },
        successCallback: response => setSelector(camelCaseRecursive(response.data.data))
      });
    request();
  };

  const hasAllSelection = (groupSelection = []) => {
    return groupSelection.some(option => option.value === ALL_SELECTED_LABEL);
  };

  const handleDisabledSelection = (options, selectedOptions) => {
    if (hasAllSelection(selectedOptions)) {
      return true;
    }
    if (selectedOptions.length === 0) return false;
    return options.value === ALL_SELECTED_LABEL;
  };

  const fetchEmployees = (inputValue, callback) => {
    debounceIndexEmployeesRequest({
      dispatch,
      params: {
        active: true,
        sort_column: 'name',
        filter_name: inputValue,
        display_length: 20,
        is_dt: false
      },
      successCallback: response => {
        const dataToGroupSearch = [{ id: 'Todos', label: 'Todos', value: 'Todos' }, ...response.data.data];
        callback(response.data.data.length > 0 ? dataToGroupSearch : []);
      }
    });
  };

  const handleNewMulti = (data, field, allowEmpty = false) => {
    const newData = data.map(element => element.value);
    if (newData.length && newData.includes(ALL_SELECTED_LABEL)) {
      newData.push('');
      return;
    }

    if (allowEmpty && !newData.length) newData.push('');
    setFieldValue(field, newData);
  };

  useEffect(searchEmployeesParams, []);

  return (
    <Form>
      <Row>
        <Col md={3} sm={3} xs={12}>
          <Field name="filterEmployeeNationalId">
            {({ field }) => (
              <FormikInput
                {...field}
                label="N° de Identificación:"
                placeholder="Sin puntos (ej: 11111111-1)"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col md={3} sm={3} xs={12}>
          <Field name="employees">
            {({ field }) => (
              <InputSelect
                {...field}
                label="Nombres y Apellidos"
                isMulti
                request={fetchEmployees}
                placeholder="Seleccione colaboradores"
                onChange={data => {
                  handleNewMulti(data || [], 'filterEmployeeIds', true);
                  setFieldValue(field.name, data || []);
                }}
                isOptionDisabled={handleDisabledSelection}
                isClearable
                menuPlacement="auto"
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
        <Col md={3} sm={3} xs={12}>
          <Field name="filterDocumentRangeDate">
            {({ field }) => (
              <FormikRangePicker
                name={field.name}
                value={field.value}
                startDateName="dateFrom"
                endDateName="dateTo"
                startDate={values.dateFrom}
                endDate={values.dateTo}
                showClearDates
              />
            )}
          </Field>
        </Col>
        <Col md={3} sm={3} xs={12}>
          <Field name="filterDocumentStatus">
            {({ field }) => (
              <FormikSelect
                {...field}
                label="Status del documento:"
                placeholder="Selecciona un estatus"
                onChange={data => {
                  setFieldValue(field.name, data ? data.value : '');
                }}
                options={documentStatus}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
                isClearable
              />
            )}
          </Field>
        </Col>
      </Row>
      <Row>
        <Col md={3} sm={3} xs={12}>
          <Field name="filterDocumentType">
            {({ field }) => (
              <FormikSelect
                {...field}
                label="Tipo de documento:"
                placeholder="Selecciona un tipo"
                onChange={data => {
                  setFieldValue(field.name, data ? data.value : '');
                }}
                options={documentTypes}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
                isClearable
              />
            )}
          </Field>
        </Col>
        <Col md={3} sm={3} xs={12}>
          <Field name="filterDocumentTemplate">
            {({ field }) => (
              <InputSelect
                {...field}
                label="Plantilla de documento:"
                placeholder="Selecciona una plantilla"
                request={fetchDocumentTemplates}
                onChange={data => {
                  setFieldValue(field.name, data ? { label: data.label, value: data.value } : '');
                  setFieldValue('filterDocumentTemplateId', data ? data.value : '');
                }}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
                menuPlacement="auto"
                isClearable
              />
            )}
          </Field>
        </Col>
        <Col md={6} sm={6} xs={12}>
          <Field name="filterDocumentAdvandceEmployeeRelatedData">
            {({ field }) => (
              <FormikSelect
                {...field}
                label="Filtro Avanzado"
                placeholder="Selecciona"
                options={selector}
                isMulti
                onChange={data => {
                  const advanceOptions = {
                    filter_active_job_managements: [],
                    filter_active_job_titles: [],
                    filter_active_cost_centers: [],
                    filter_active_branch_offices: []
                  };

                  if (data) {
                    data.forEach(item => {
                      advanceOptions[item.queryValue].push(item.id);
                    });
                  }

                  const options = Object.keys(advanceOptions);
                  options.forEach(item => {
                    setFieldValue(CamelCase(item), advanceOptions[item]);
                  });
                }}
                error={getIn(errors, field.name)}
                touched={getIn(touched, field.name)}
              />
            )}
          </Field>
        </Col>
      </Row>
      <Row className="d-flex justify-content-end">
        <Col md={2} sm={3} xs={12}>
          <ButtonTooltip variant="primary" className="btn-block" text="Filtrar" type="submit">
            Filtrar
          </ButtonTooltip>
        </Col>
      </Row>
    </Form>
  );
};

const setInitialValues = () => {
  return {
    filterEmployeeNationalId: '',
    filterDocumentTemplate: '',
    filterDocumentTemplateId: '',
    filterEmployeeIds: [],
    filterDocumentStatus: '',
    filterDocumentType: '',
    filterDocumentName: '',
    filterDocumentAdvandceEmployeeRelatedData: [],
    dateFrom: '',
    dateTo: ''
  };
};

const handleSubmit = (values, { props }) => {
  const { formRequest } = props;
  formRequest(values);
};

export default withFormik({
  mapPropsToValues: () => setInitialValues(),
  handleSubmit,
  enableReinitialize: false
})(DocumentFilter);
